import { useCurrentCommunityId, useCommunityDetail } from '@communities/community/hooks';
import { useCommunityTopics } from '@communities/content-management/hooks';
import { HeroBackground } from '@core/components/HeroBackground';
import { CommunityModerationHeaderSection } from '@moderation/components/CommunityModerationHeaderSection';
import { ModerationQueueTable } from '@moderation/components/ModerationQueueTable';
import { Heading } from '@soteria/design-system-ve';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityModerationDashboardPage.css';

/**
 * Formats seconds into a human-readable duration string
 */
function formatSLADuration(seconds: number): string {
  if (seconds <= 0) return 'Not set';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}

/**
 * Community Moderation Dashboard Page
 *
 * Main landing page for community moderation showing queue overview and stats
 */
export function CommunityModerationDashboardPage() {
  // Scroll to top when page loads for accessibility and standard web practice
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const communityId = useCurrentCommunityId();
  const { community, loading: communityLoading } = useCommunityDetail();

  // Fetch configured topics for the community
  const { topics: configuredTopics, loading: topicsLoading } = useCommunityTopics(communityId);

  // Get moderation stats from community data (fetched as part of community query)
  const topicStats = useMemo(() => community?.moderationStats || [], [community?.moderationStats]);

  // Calculate aggregate stats from topic stats
  const stats = useMemo(() => {
    if (!topicStats.length) {
      return {
        totalCases: 0,
        casesOpen: 0,
        activeModerators: 0,
        casesCompleted: 0,
      };
    }

    const totalOpen = topicStats.reduce((sum, topic) => sum + topic.openCasesCount, 0);
    const totalAssigned = topicStats.reduce((sum, topic) => sum + topic.assignedCasesCount, 0);
    const totalInSLA = topicStats.reduce((sum, topic) => sum + topic.inSLACases, 0);

    return {
      totalCases: totalOpen + totalAssigned,
      casesOpen: totalOpen,
      activeModerators: 0, // API integration pending
      casesCompleted: totalInSLA,
    };
  }, [topicStats]);

  // Transform API data to table format, merging configured topics with stats
  const topics = useMemo(() => {
    // Create a map of topic stats for quick lookup
    const statsMap = new Map(topicStats.map(stat => [stat.topicName, stat]));

    // Map configured topics and enrich with stats if available
    return configuredTopics.map(configTopic => {
      const stats = statsMap.get(configTopic.name);

      return {
        name: configTopic.name,
        caseSLA: formatSLADuration(configTopic.humanModerationCasesSlaInSeconds),
        openCases: stats?.openCasesCount ?? 0,
        nearSLA: stats?.nearSLACases ?? 0,
        pastSLA: stats?.outOfSLACases ?? 0,
      };
    });
  }, [configuredTopics, topicStats]);

  const handleStartModerate = (topic: { name: string; caseSLA: string; openCases: number }) => {
    // Navigate to moderation work queue page
    const topicId = topic.name.toLowerCase().replace(/\s+/g, '-');

    navigate(`/communities/${communityId}/moderation/${topicId}/queue`);
  };

  const isLoading = communityLoading || topicsLoading;

  return (
    <div>
      {/* Hero Background with Community Image */}
      <HeroBackground imageUrl={community?.superHeroArtUrl} />

      {/* Header Section */}
      <CommunityModerationHeaderSection community={community} isLoading={isLoading} stats={stats} />

      {/* Main Content Section */}
      <div className={styles.contentSection}>
        <div className={styles.contentContainer}>
          {/* Section Header */}
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Heading level="h2">
                {community?.name} Moderation Queue ({topics.length})
              </Heading>
              <div className={styles.sectionDescription}>Monitor and moderate content across topics with real-time case tracking and moderator activity.</div>
            </div>
          </div>

          {/* Topics Table */}
          <ModerationQueueTable isLoading={isLoading} topics={topics} onStartModerate={handleStartModerate} />
        </div>
      </div>
    </div>
  );
}
